import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ClipboardCheck,
    Receipt,
    Wallet,
    AlertTriangle,
    TrendingUp,
    TrendingDown,
    ChevronRight,
    Camera,
    Mic,
    Sparkles,
    Store,
} from "lucide-react";
import { api, fmtEur, fmtDate, todayISO } from "../lib/api";
import { toast } from "sonner";
import { clubName } from "../lib/clubApi";
import VoiceHaccp from "../components/VoiceHaccp";

export default function Dashboard({ user, shop }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [voiceOpen, setVoiceOpen] = useState(false);

    const loadDash = () => {
        api.get("/dashboard")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Errore caricamento dashboard"))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadDash();
    }, []);

    if (loading)
        return (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 stagger">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="card-soft p-5 animate-pulse h-28" />
                ))}
            </div>
        );

    if (!data) return null;

    const pct = data.checklist_totali
        ? Math.round((data.checklist_completate / data.checklist_totali) * 100)
        : 0;
    const saldoPositive = (data.mese?.saldo ?? 0) >= 0;

    const stats = [
        {
            label: "Controlli HACCP oggi",
            value: `${data.checklist_completate}/${data.checklist_totali}`,
            sub: `${pct}% completati`,
            icon: ClipboardCheck,
            link: "/haccp",
            testid: "stat-haccp",
            tint: "bg-forest-50 text-forest",
        },
        {
            label: "Spese del mese",
            value: fmtEur(data.mese?.spese_totali),
            sub: `${data.mese?.count || 0} movimenti`,
            icon: TrendingDown,
            link: "/spese",
            testid: "stat-spese",
            tint: "bg-orange-50 text-terracotta",
        },
        {
            label: "Incassi del mese",
            value: fmtEur(data.mese?.incassi_totali),
            sub: "Mese in corso",
            icon: TrendingUp,
            link: "/spese",
            testid: "stat-incassi",
            tint: "bg-emerald-50 text-emerald-700",
        },
        {
            label: "Saldo del mese",
            value: fmtEur(data.mese?.saldo),
            sub: saldoPositive ? "In positivo" : "In negativo",
            icon: Wallet,
            link: "/spese",
            testid: "stat-saldo",
            tint: saldoPositive ? "bg-forest-50 text-forest" : "bg-red-50 text-red-700",
        },
    ];

    return (
        <div className="space-y-6 pb-6" data-testid="dashboard-page">
            <VoiceHaccp
                open={voiceOpen}
                onClose={() => setVoiceOpen(false)}
                onApplied={loadDash}
                dataGiorno={todayISO()}
            />

            {/* Big voice CTA at top — visible immediately on app open */}
            <button
                type="button"
                onClick={() => setVoiceOpen(true)}
                className="w-full flex items-center gap-4 p-5 rounded-3xl bg-gradient-to-br from-forest-700 via-forest-800 to-forest-900 text-white shadow-xl hover:shadow-2xl transition-all active:scale-[0.99] relative overflow-hidden"
                data-testid="dashboard-voice-open"
            >
                {/* Decorative pulse rings */}
                <span className="absolute -right-12 -top-12 w-40 h-40 rounded-full bg-white/5" />
                <span className="absolute -right-6 -bottom-10 w-28 h-28 rounded-full bg-white/5" />
                <div className="w-16 h-16 rounded-2xl bg-white/15 grid place-items-center shrink-0 relative z-10">
                    <Mic size={32} strokeWidth={2.2} />
                </div>
                <div className="text-left flex-1 min-w-0 relative z-10">
                    <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-90 bg-white/15 px-2 py-0.5 rounded-full">
                        <Sparkles size={10} /> Nuovo · AI
                    </div>
                    <p className="font-display text-xl font-semibold leading-snug mt-1.5">Compila l'HACCP parlando</p>
                    <p className="text-sm opacity-95 mt-0.5">
                        Tocca e dimmi cosa hai controllato: temperature, pulizia, anomalie. L'AI compila i campi giusti.
                    </p>
                </div>
                <ChevronRight size={20} className="opacity-70 relative z-10 shrink-0 hidden sm:block" />
            </button>

            {/* Club soci quick access (every tenant, named after the shop) */}
            <Link
                to="/club"
                data-testid="dashboard-samir-club-cta"
                className="w-full flex items-center gap-4 p-5 rounded-3xl bg-stone-900 text-white shadow-xl hover:shadow-2xl transition-all active:scale-[0.99] relative overflow-hidden"
            >
                <span className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/5" />
                <div className="w-14 h-14 rounded-2xl bg-white/10 grid place-items-center shrink-0 relative z-10">
                    <span className="text-3xl leading-none" role="img" aria-label="apple">🍎</span>
                </div>
                <div className="text-left flex-1 min-w-0 relative z-10">
                    <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-90 bg-white/15 px-2 py-0.5 rounded-full">
                        <Store size={10} /> Negozio · Fedeltà
                    </div>
                    <p className="font-display text-xl font-semibold leading-snug mt-1.5 truncate">{clubName(shop)}</p>
                    <p className="text-sm opacity-80 mt-0.5">
                        Tessera fedeltà del tuo negozio: gestisci soci, vendite e visite.
                    </p>
                </div>
                <ChevronRight size={20} className="opacity-70 relative z-10 shrink-0 hidden sm:block" />
            </Link>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 stagger">
                {stats.map((s) => (
                    <Link
                        key={s.label}
                        to={s.link}
                        data-testid={s.testid}
                        className="card-soft p-5 hover:-translate-y-0.5 hover:shadow-md transition-all"
                    >
                        <div className="flex items-start justify-between">
                            <div className={`w-10 h-10 rounded-xl grid place-items-center ${s.tint}`}>
                                <s.icon size={18} />
                            </div>
                            <ChevronRight size={16} className="text-stone-300" />
                        </div>
                        <p className="text-xs font-bold uppercase tracking-widest text-stone-500 mt-4">
                            {s.label}
                        </p>
                        <p className="font-display text-2xl font-semibold text-forest-dark mt-1">
                            {s.value}
                        </p>
                        <p className="text-xs text-stone-500 mt-0.5">{s.sub}</p>
                    </Link>
                ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
                <Link
                    to="/haccp"
                    className="card-soft p-6 lg:col-span-2 hover:-translate-y-0.5 transition-all"
                    data-testid="card-haccp-progress"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="overline text-xs font-bold uppercase tracking-widest text-forest-500">
                                Autocontrollo giornaliero
                            </p>
                            <h2 className="font-display text-2xl font-semibold text-forest-dark mt-1">
                                Checklist HACCP di oggi
                            </h2>
                            <p className="text-sm text-stone-500 mt-1">
                                {data.checklist_completate} su {data.checklist_totali} attività registrate
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="font-display text-4xl font-bold text-forest">{pct}%</p>
                        </div>
                    </div>
                    <div className="mt-4 h-3 bg-bone-2 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-forest transition-all duration-500"
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                        <span className="btn-primary text-sm">Apri checklist</span>
                    </div>
                </Link>

                <Link
                    to="/fatture"
                    className="card-soft p-6 bg-gradient-to-br from-[#FFFCF6] to-[#F4EFE0] border-[#E4DEC3]"
                    data-testid="card-quick-upload"
                >
                    <div className="w-12 h-12 rounded-xl bg-accent-warm grid place-items-center text-white">
                        <Camera size={22} />
                    </div>
                    <h3 className="font-display text-lg font-semibold text-forest-dark mt-3">
                        Scansiona fattura
                    </h3>
                    <p className="text-sm text-stone-600 mt-1">
                        Fotografa fatture e scontrini, l'AI estrae i dati e li archivia per te.
                    </p>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-terracotta-dark mt-4">
                        Carica ora <ChevronRight size={14} />
                    </span>
                </Link>
            </div>

            <div className="card-soft p-6" data-testid="card-recent-invoices">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-xl font-semibold text-forest-dark">
                        Fatture recenti
                    </h2>
                    <Link to="/fatture" className="text-sm font-semibold text-forest hover:underline">
                        Vedi tutte
                    </Link>
                </div>
                {!data.fatture_recenti?.length ? (
                    <div className="text-center py-10 text-stone-500">
                        <Receipt size={32} className="mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Nessuna fattura caricata. Inizia scansionando la prima!</p>
                    </div>
                ) : (
                    <ul className="divide-y divide-[color:var(--border-soft)]">
                        {data.fatture_recenti.map((f) => (
                            <li
                                key={f.id}
                                className="py-3 flex items-center justify-between gap-3"
                                data-testid={`recent-invoice-${f.id}`}
                            >
                                <div className="min-w-0">
                                    <p className="font-semibold text-forest-dark truncate">
                                        {f.fornitore || "Fornitore sconosciuto"}
                                    </p>
                                    <p className="text-xs text-stone-500">
                                        {fmtDate(f.data_fattura)} · {f.categoria}
                                    </p>
                                </div>
                                <p className="font-display font-semibold text-forest">{fmtEur(f.totale)}</p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {data.non_conformita_totali > 0 && (
                <div
                    className="card-soft p-5 flex items-center gap-4 bg-[#FFF8EE] border-[#E9A846]/30"
                    data-testid="card-non-conformity-alert"
                >
                    <div className="w-10 h-10 rounded-xl bg-[#E9A846]/15 grid place-items-center text-[#A87214]">
                        <AlertTriangle size={20} />
                    </div>
                    <div>
                        <p className="font-display font-semibold text-forest-dark">
                            {data.non_conformita_totali} non conformità registrate
                        </p>
                        <p className="text-sm text-stone-500">
                            Controlla lo storico e le azioni correttive.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
